'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface ProductCategory {
  id: string;
  name: string;
  description: string;
  image: string;
  alt: string;
  href: string;
  typicalItems: string[];
}

const productCategories: ProductCategory[] = [
  {
    id: 'mechanical-parts',
    name: 'Structural System Parts',
    description: 'Comprehensive range of structural components for elevator systems including guide rails, ropes, sheaves, and structural assemblies.',
    image: '/assets/images/mechanical_component_line_art.png',
    alt: 'Line art illustration of structural elevator components including gears and structural elements',
    href: '/mechanical-parts',
    typicalItems: [
      'Car Frame',
      'Guide Rails',
      'Rail Brackets',
      'Wire Ropes',
      'Sheaves',
      'Safety Components',
      'Door Operators',
      'Cab Fixtures'
    ]
  },
  {
    id: 'electrical-parts',
    name: 'Electrical System Parts',
    description: 'Complete selection of electrical components including controllers, panels, switches, sensors, and wiring assemblies for elevator systems.',
    image: '/assets/images/electrical_component_line_art.png',
    alt: 'Line art illustration of electrical elevator components including circuit boards and control systems',
    href: '/electrical-parts',
    typicalItems: [
      'Controllers',
      'Control Panels',
      'Position Sensors',
      'Call Buttons',
      'LED Indicators',
      'Wiring Harnesses',
      'Safety Switches',
      'Communication Systems'
    ]
  },
  {
    id: 'power-parts',
    name: 'Power System Parts',
    description: 'Power system components including motors, drives, transformers, power supplies, and energy management systems for elevator installations.',
    image: '/assets/images/power_component_line_art.png',
    alt: 'Line art illustration of power system components including motors and electrical power units',
    href: '/power-parts',
    typicalItems: [
      'Traction Motors',
      'Variable Frequency Drives',
      'Transformers',
      'Power Supplies',
      'Regenerative Units',
      'Contactors',
      'Motor Starters',
      'Emergency Power Systems'
    ]
  },
  {
    id: 'cabs-gates',
    name: 'Cabs & Gates',
    description: 'Complete elevator cab interiors and gate systems including panels, doors, fixtures, and safety gate assemblies for residential and commercial installations.',
    image: '/assets/images/Category_Image_Residential_Cab-1770883259140.png',
    alt: 'Residential elevator cab sketch showing interior design and gate components',
    href: '/cabs-gates',
    typicalItems: [
      'Cab Panels',
      'Ceiling Systems',
      'Flooring',
      'Handrails',
      'Gate Assemblies',
      'Gate Operators',
      'Interior Lighting',
      'Ventilation Systems'
    ]
  }
];

export default function ProductsContent() {
  return (
    <div className="min-h-screen">
      {/* Hero Section — red/black color blocking */}
      <section className="relative bg-black py-20 lg:py-28 overflow-hidden">
        {/* Red geometric accent — left border bar */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-red-600" />
        {/* Red geometric accent — diagonal corner */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-red-600 opacity-10" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            {/* Category label */}
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-0.5 bg-red-600" />
              <span className="text-red-500 text-xs font-bold uppercase tracking-widest">Product Catalog</span>
            </div>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold mb-6 text-white leading-tight">
              Product<br /><span className="text-red-600">Categories</span>
            </h1>
            <p className="font-body text-lg lg:text-xl text-gray-300 leading-relaxed">
              Whether you need a complete Structural, Power, or Electrical package for a residential or light commercial elevator with up to six stops, or you only need a part to keep your system running smoothly — our stock and inventory are built for both.
            </p>
          </div>
        </div>
      </section>

      {/* Product Categories Grid */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.map((category) => (
              <Link
                key={category.id}
                href={category.href}
                className="bg-white border-2 border-black overflow-hidden hover:shadow-xl transition-all duration-300 block group"
              >
                {/* Red top accent bar */}
                <div className="h-1.5 bg-red-600 w-full" />

                {/* Category Image */}
                <div className="relative h-52 overflow-hidden bg-gray-50 flex items-center justify-center">
                  <AppImage
                    src={category.image}
                    alt={category.alt}
                    width={400}
                    height={300}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Category Details */}
                <div className="p-5 border-t-2 border-black">
                  {/* Category name */}
                  <h3 className="font-heading text-xl font-bold text-black mb-3 leading-tight">
                    {category.name}
                  </h3>

                  {/* Typical Items List */}
                  <div className="mb-5">
                    <h4 className="text-xs font-bold text-red-600 mb-2 uppercase tracking-widest">
                      Typical Items:
                    </h4>
                    <ul className="space-y-1">
                      {category.typicalItems.map((item, index) => (
                        <li
                          key={index}
                          className="font-body text-gray-600 text-sm flex items-center gap-2"
                        >
                          <span className="w-1.5 h-1.5 bg-red-600 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Explore CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-sm font-bold text-black uppercase tracking-wide">Explore Products</span>
                    <div className="w-8 h-8 bg-red-600 flex items-center justify-center group-hover:bg-red-700 transition-colors">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section — black with red accent */}
      <section className="relative bg-black py-16 lg:py-24 overflow-hidden">
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-600 opacity-10" style={{ clipPath: 'polygon(0 100%, 100% 100%, 0 0)' }} />
        <div className="absolute top-0 right-0 w-2 h-full bg-red-600" />
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center space-y-6 relative z-10">
          <div className="inline-flex items-center gap-2 justify-center mb-2">
            <div className="w-8 h-0.5 bg-red-600" />
            <span className="text-red-500 text-xs font-bold uppercase tracking-widest">Expert Support</span>
            <div className="w-8 h-0.5 bg-red-600" />
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white">
            Need Help Finding the Right Parts?
          </h2>
          <p className="font-body text-lg text-gray-300 leading-relaxed">
            Our team of experts can help you identify the exact components you need for your elevator system. Contact us for personalized assistance.
          </p>
          <Link
            href="/contact"
            className="inline-block px-8 py-4 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:border-red-700 hover:-translate-y-1 hover:shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}